import { useToast } from "@/components/ui/use-toast";
import { Module, PATH_PAYROLL_PREVIEW } from "@/constants";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { PayrollsService } from "@/services/payrolls";
import { useQueryClient } from "@tanstack/react-query";
import { XIcon } from "lucide-react";
import { memo, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router-dom";
import { usePayrollParams } from "../hooks/usePayrollParams";
import { PayrollConfirmButton } from "./PayrollConfirmButton";

export const PayrollCloseButton = memo(() => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const { payrollId } = usePayrollParams();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const mutationFn = async (payrollId: string) => {
    await PayrollsService.closePayroll(payrollId);
  };

  const mutation = useMutationWithErrorHandling({
    errorTitle: t("payrolls.errors.close"),
    mutationFn,
    onSuccess: () => {
      toast({
        title: t("payrolls.success.close"),
      });
      queryClient.invalidateQueries({
        queryKey: [Module.Payrolls, payrollId],
      });
      navigate(
        generatePath(PATH_PAYROLL_PREVIEW, {
          payrollId,
        }),
      );
    },
  });

  const onConfirm = useCallback(() => {
    mutation.mutateAsync(payrollId);
  }, [mutation, payrollId]);

  return (
    <PayrollConfirmButton
      icon={XIcon}
      translationKey="payrolls.closePayroll"
      open={open || mutation.isPending}
      onOpenChange={setOpen}
      onConfirm={onConfirm}
      isPending={mutation.isPending}
    />
  );
});
